import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useListUiState } from '../hooks/useListUiState'
import { useScrollRestoration, clearScrollRestoration } from '../hooks/useScrollRestoration'
import { buildReturnToFromLocation } from '../utils/navigationReturnTo'
import EmailLoginModal from '../components/EmailLoginModal'
import { getExercisesByAuthor, getAllExercisesForTeachers, deleteExercise, duplicateExercise, refreshAllAutoTags, getVideoRanksForExercises, setExercisePublicPreview } from '../services/exerciseService'
import { getPendingEstablishmentRequests, getPendingClassRequests } from '../services/referenceDataService'
import { getAuthErrorMessage } from '../utils/errorHandler'
import { getExerciseDisplayTitle, getExerciseSubtitle, getComposerDisplayName, getComposerDisplayNameShort, getComposerFamilyName, getExerciseGroupRank } from '../utils/exerciseDisplay'
import { getGlobalNotionsFromAutoTags, getTagForNotionLabel } from '../utils/globalNotions'
import { formatTagForDisplay } from '../utils/tagFormatter'
import { categorizeTags, TAG_CATEGORY_LABELS } from '../utils/tagCategories'
import { CLASSICAL_FORMATIONS, CLASSICAL_GENRES, inferFormationsFromWorkTitle, inferGenreFromWorkTitle } from '../data/formations'
import { FormationInstrumentPicker } from '../components/FormationInstrumentPicker'
import ProfileModal from '../components/ProfileModal'
import EditTagsModal from '../components/EditTagsModal'
import AssignToClassModal from '../components/AssignToClassModal'
import ExerciseStatsModal from '../components/ExerciseStatsModal'
import { BarChart3, Eye } from 'lucide-react'
import { canAccessParcoursImagesEditor, canViewApiUsage } from '../config/adminAllowlist'
import { getUsage, isOpusApiConfigured } from '../services/opusApi'
import { getDisplayDifficulty } from '../utils/difficultyFromContent'
import { getCodexContextLinksForExercise, getCodexUrlForEntry } from '../utils/codexContextLinks'
import { asDisplayText } from '../utils/safeText'
import {
  addExerciseToLiveSessionDraft,
  loadLiveSessionDraft
} from '../utils/liveSessionDraft'
import { getTeacherClasses } from '../services/teacherClassService'
import {
  getAssignmentsByClass,
  getExerciseIdsFromAssignment,
  ASSIGNMENTS_BY_CLASS_UI_LIMIT
} from '../services/assignmentService'
import NotionPillsRow from '../components/NotionPillsRow'
import { useBackdropDismiss } from '../hooks/useBackdropDismiss'
import './Dashboard.css'

const DIFFICULTIES = ['facile', 'intermédiaire', 'difficile', 'expert']
const TAG_CATEGORY_KEYS = Object.keys(TAG_CATEGORY_LABELS)
const COMPOSERS_VISIBLE_INITIAL = 12
/** Nombre de cartes exercice par page (liste filtrée / triée). */
const DASHBOARD_EXERCISES_PAGE_SIZE = 48

/** Filtres / tri / pagination Dashboard — synchronisés avec l’URL. */
const DASHBOARD_LIST_FIELDS = {
  scope: { type: 'string', default: 'mine' },
  q: { type: 'string', default: '', debounce: true },
  difficulty: { type: 'string', default: '' },
  composer: { type: 'string', default: '' },
  tags: { type: 'string[]', default: [] },
  tagMode: { type: 'string', default: 'any' },
  genre: { type: 'string', default: '' },
  formations: { type: 'string[]', default: [] },
  author: { type: 'string', default: '' },
  status: { type: 'string', default: 'all' },
  vitrine: { type: 'boolean', default: false },
  horizons: { type: 'string', default: 'classic' },
  sort: { type: 'string', default: 'date' },
  page: { type: 'number', default: 0 },
  hwClass: { type: 'string', default: '' },
  hwOnly: { type: 'boolean', default: false },
}

const TOOLTIP_HIDE_DELAY = 180

const notionSlug = (s) =>
  (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')

function DashboardCardTooltipZone({ exercise, exercises, formatDate, onDifficultyFilter, onTagFilter, selectedDifficulty, selectedTags }) {
  const [showTooltip, setShowTooltip] = useState(false)
  const hideTimeoutRef = useRef(null)

  useEffect(() => {
    return () => { if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current) }
  }, [])

  const notions = getGlobalNotionsFromAutoTags(exercise.autoTags)
  const hasTags = exercise.autoTags && exercise.autoTags.length > 0
  const hasNotions = notions.length > 0
  const difficulty = exercise.metadata?.difficulty
  const displayDifficulty = getDisplayDifficulty(difficulty)
  const pillItems = [
    ...(displayDifficulty ? [{ label: displayDifficulty, isDifficulty: true }] : []),
    ...notions.map((label) => ({ label }))
  ]
  const sub = getExerciseSubtitle(exercise, exercises)

  if (!hasTags && !hasNotions && !displayDifficulty) return null

  const handleEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    setShowTooltip(true)
  }

  const handleLeave = () => {
    hideTimeoutRef.current = setTimeout(() => setShowTooltip(false), TOOLTIP_HIDE_DELAY)
  }

  const renderPill = (item, i) => {
    const { label, isDifficulty } = item
    const tagForNotion = !isDifficulty ? getTagForNotionLabel(exercise.autoTags, label) : null
    const canFilter = isDifficulty ? Boolean(onDifficultyFilter) : Boolean(tagForNotion && onTagFilter)
    if (isDifficulty) {
      return (
        <span
          key="diff"
          className={`dashboard-card-pill dashboard-card-pill--difficulty ${label}${canFilter ? ' dashboard-card-pill-clickable' : ''}${selectedDifficulty === label ? ' active' : ''}`}
          role={canFilter ? 'button' : undefined}
          tabIndex={canFilter ? 0 : undefined}
          onClick={(e) => {
            if (!canFilter) return
            e.stopPropagation()
            onDifficultyFilter(label)
          }}
          onKeyDown={(e) => {
            if (!canFilter) return
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onDifficultyFilter(label)
            }
          }}
          aria-pressed={canFilter ? selectedDifficulty === label : undefined}
        >
          {label}
        </span>
      )
    }
    return (
      <span
        key={i}
        className={`dashboard-card-pill dashboard-card-pill--notion${canFilter ? ' dashboard-card-pill-clickable' : ''}${tagForNotion && selectedTags.includes(tagForNotion) ? ' active' : ''}`}
        data-notion={notionSlug(label)}
        role={canFilter ? 'button' : undefined}
        tabIndex={canFilter ? 0 : undefined}
        onClick={(e) => {
          if (!canFilter) return
          e.stopPropagation()
          onTagFilter(tagForNotion)
        }}
        onKeyDown={(e) => {
          if (!canFilter) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onTagFilter(tagForNotion)
          }
        }}
        aria-pressed={canFilter ? Boolean(tagForNotion && selectedTags.includes(tagForNotion)) : undefined}
      >
        {label}
      </span>
    )
  }

  return (
    <div
      className="dashboard-card-pills-zone"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <NotionPillsRow
        items={pillItems}
        containerClassName="dashboard-card-pills-row"
        moreClassName="dashboard-card-pill dashboard-card-pill--more"
        renderPill={renderPill}
      />

      {showTooltip && (
        <div
          className="dashboard-hover-tooltip"
          role="tooltip"
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={handleEnter}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <div className="dashboard-hover-tooltip-inner">
            <div className="dashboard-hover-tooltip-header">
              <span className={`dashboard-hover-tooltip-status ${exercise.status || 'draft'}`}>
                {exercise.status === 'published' ? 'Publié' : 'Brouillon'}
              </span>
              <span className="dashboard-hover-tooltip-date">{formatDate(exercise.createdAt)}</span>
            </div>

            <div className="dashboard-hover-tooltip-meta">
              {sub && <span className="dashboard-hover-tooltip-subtitle">{sub}</span>}
              <span className="dashboard-hover-tooltip-markers">
                {exercise.markers?.length || 0} marqueur{exercise.markers?.length !== 1 ? 's' : ''}
              </span>
            </div>

            {(displayDifficulty || hasNotions) && (
              <div className="dashboard-hover-tooltip-pills">
                {displayDifficulty && (
                  <span className={`dashboard-card-pill dashboard-card-pill--difficulty ${displayDifficulty}`}>{displayDifficulty}</span>
                )}
                {notions.map((label, i) => (
                  <span key={i} className="dashboard-card-pill dashboard-card-pill--notion" data-notion={notionSlug(label)}>{label}</span>
                ))}
              </div>
            )}

            {hasTags && (
              <div className="dashboard-hover-tooltip-tags">
                <span className="dashboard-hover-tooltip-tags-label">Tags ({exercise.autoTags.length})</span>
                <div className="dashboard-hover-tooltip-tags-list">
                  {exercise.autoTags.map((tag, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`dashboard-hover-tooltip-tag${selectedTags.includes(tag) ? ' active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        onTagFilter(tag)
                      }}
                      aria-pressed={selectedTags.includes(tag)}
                    >
                      {formatTagForDisplay(tag)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function Dashboard() {
  const { user, userData, loading: authLoading, signInWithGoogle, logout, isAdminTeacher } = useAuth()
  const [showEmailLoginModal, setShowEmailLoginModal] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const [exercises, setExercises] = useState([])
  const [loading, setLoading] = useState(true)
  const [deletingId, setDeletingId] = useState(null)
  const [duplicatingId, setDuplicatingId] = useState(null)
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false)
  const [openMenuId, setOpenMenuId] = useState(null)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [editTagsExercise, setEditTagsExercise] = useState(null)
  const [assignExercise, setAssignExercise] = useState(null)
  const [statsExercise, setStatsExercise] = useState(null)
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0)
  const menuRefs = useRef({})
  const userMenuRef = useRef(null)

  const {
    values: listValues,
    inputValues: listInput,
    setField: setListField,
    setFields: setListFields,
    setDraft: setListDraft,
    reset: resetListFilters,
    hasActive: listHasActive,
  } = useListUiState({
    fields: DASHBOARD_LIST_FIELDS,
    debounceMs: 300,
    resetPageKey: 'page',
  })

  const exerciseFilter = listValues.scope === 'all' ? 'all' : 'mine'
  const setExerciseFilter = useCallback((scope) => {
    setListFields({
      scope: scope === 'all' ? 'all' : 'mine',
      author: '',
    })
  }, [setListFields])

  const searchText = listInput.q
  const setSearchText = useCallback((v) => {
    setListDraft('q', typeof v === 'function' ? v(listInput.q) : v)
  }, [setListDraft, listInput.q])
  const debouncedSearchText = listValues.q
  const selectedDifficulty = listValues.difficulty
  const setSelectedDifficulty = useCallback((v) => setListField('difficulty', v), [setListField])
  const selectedComposer = listValues.composer
  const setSelectedComposer = useCallback((v) => setListField('composer', v), [setListField])
  const selectedTags = listValues.tags
  const setSelectedTags = useCallback((v) => setListField('tags', v), [setListField])
  const tagMatchMode = listValues.tagMode
  const setTagMatchMode = useCallback((v) => setListField('tagMode', v), [setListField])
  const selectedGenre = listValues.genre
  const setSelectedGenre = useCallback((v) => setListField('genre', v), [setListField])
  const selectedFormations = listValues.formations
  const setSelectedFormations = useCallback((v) => setListField('formations', v), [setListField])
  const selectedAuthorId = listValues.author
  const setSelectedAuthorId = useCallback((v) => setListField('author', v), [setListField])
  const selectedStatus = listValues.status
  const setSelectedStatus = useCallback((v) => setListField('status', v), [setListField])
  const selectedVitrineOnly = listValues.vitrine
  const setSelectedVitrineOnly = useCallback((v) => setListField('vitrine', v), [setListField])
  const horizonsFilter = listValues.horizons
  const setHorizonsFilter = useCallback((v) => setListField('horizons', v), [setListField])
  const sortBy = listValues.sort
  const setSortBy = useCallback((v) => setListField('sort', v), [setListField])
  const homeworkFilterClassId = listValues.hwClass
  const setHomeworkFilterClassId = useCallback((v) => setListField('hwClass', v), [setListField])
  const homeworkRestrictOnlyAssigned = listValues.hwOnly
  const setHomeworkRestrictOnlyAssigned = useCallback((v) => setListField('hwOnly', v), [setListField])
  const dashboardResultsPage = listValues.page
  const setDashboardResultsPage = useCallback((v) => setListField('page', v), [setListField])

  const [vitrineTogglingId, setVitrineTogglingId] = useState(null)
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)
  const dismissFilterDrawer = useCallback(() => setIsFilterDrawerOpen(false), [])
  const filterDrawerBackdropDismiss = useBackdropDismiss(dismissFilterDrawer)
  const [videoRanksMap, setVideoRanksMap] = useState(() => new Map())
  const [expandedTagSections, setExpandedTagSections] = useState(() => new Set())
  const [apiUsage, setApiUsage] = useState(null)
  const [apiUsageLoading, setApiUsageLoading] = useState(false)
  const [isRefreshingTags, setIsRefreshingTags] = useState(false)
  const [refreshTagsMessage, setRefreshTagsMessage] = useState(null)
  const [composersExpanded, setComposersExpanded] = useState(false)
  const [liveDraftInfo, setLiveDraftInfo] = useState({
    selectedClassId: '',
    selectedClassLabel: '',
    queueCount: 0
  })
  const [liveDraftToast, setLiveDraftToast] = useState('')
  const [dashboardTeacherClasses, setDashboardTeacherClasses] = useState([])
  const [homeworkClassAssignments, setHomeworkClassAssignments] = useState([])
  const [homeworkAssignmentsLoading, setHomeworkAssignmentsLoading] = useState(false)
  const dashboardResultsRef = useRef(null)
  const skipDashboardPaginationScrollRef = useRef(true)
  const [isMobileLayout, setIsMobileLayout] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  )

  useScrollRestoration('dashboard', { ready: !loading })
  const dashboardReturnTo = useMemo(() => buildReturnToFromLocation(location), [location])

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const handler = () => setIsMobileLayout(mq.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  useEffect(() => {
    if (authLoading) return
    if (!user) return
    loadExercises()
  }, [user, authLoading, exerciseFilter])


  useEffect(() => {
    if (!user || userData?.role !== 'teacher') return
    Promise.all([getPendingEstablishmentRequests(), getPendingClassRequests()])
      .then(([est, cls]) => setPendingRequestsCount((est?.length ?? 0) + (cls?.length ?? 0)))
      .catch(() => setPendingRequestsCount(0))
  }, [user, userData?.role])

  useEffect(() => {
    if (!user?.uid || userData?.role !== 'teacher') {
      setDashboardTeacherClasses([])
      return
    }
    getTeacherClasses(user.uid)
      .then(setDashboardTeacherClasses)
      .catch(() => setDashboardTeacherClasses([]))
  }, [user?.uid, userData?.role])

  useEffect(() => {
    if (!homeworkFilterClassId) {
      setHomeworkClassAssignments([])
      setHomeworkAssignmentsLoading(false)
      return
    }
    let cancelled = false
    setHomeworkAssignmentsLoading(true)
    getAssignmentsByClass(homeworkFilterClassId, ASSIGNMENTS_BY_CLASS_UI_LIMIT)
      .then((list) => {
        if (!cancelled) setHomeworkClassAssignments(list || [])
      })
      .catch((err) => {
        console.error('Dashboard: chargement devoirs par classe', err)
        if (!cancelled) setHomeworkClassAssignments([])
      })
      .finally(() => {
        if (!cancelled) setHomeworkAssignmentsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [homeworkFilterClassId])

  const refreshLiveDraftInfo = useCallback(() => {
    const draft = loadLiveSessionDraft()
    const classId = draft.selectedClassId || ''
    const classLabel = draft.selectedClassLabel || ''
    const queue = classId ? (Array.isArray(draft.queueByClass?.[classId]) ? draft.queueByClass[classId] : []) : []
    setLiveDraftInfo({
      selectedClassId: classId,
      selectedClassLabel: classLabel,
      queueCount: queue.length
    })
  }, [])

  useEffect(() => {
    refreshLiveDraftInfo()
  }, [refreshLiveDraftInfo])

  const loadApiUsage = useCallback(async () => {
    if (!isOpusApiConfigured() || !canViewApiUsage(user?.email, user?.uid)) return
    setApiUsageLoading(true)
    try {
      const data = await getUsage()
      setApiUsage(data ?? { available: false })
    } catch {
      setApiUsage({ available: false })
    } finally {
      setApiUsageLoading(false)
    }
  }, [user?.email, user?.uid])

  useEffect(() => {
    if (canViewApiUsage(user?.email, user?.uid) && isOpusApiConfigured()) {
      loadApiUsage()
    } else {
      setApiUsage(null)
    }
  }, [user?.email, user?.uid, loadApiUsage])

  useEffect(() => {
    if (!liveDraftToast) return undefined
    const timeoutId = window.setTimeout(() => setLiveDraftToast(''), 2600)
    return () => window.clearTimeout(timeoutId)
  }, [liveDraftToast])

  // Fermer les menus au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Fermer le menu utilisateur
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false)
      }
      
      // Fermer les menus contextuels des cartes
      Object.keys(menuRefs.current).forEach((id) => {
        if (menuRefs.current[id] && !menuRefs.current[id].contains(event.target)) {
          setOpenMenuId(null)
        }
      })
      
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const loadExercises = async () => {
    if (!user) return
    try {
      setLoading(true)
      const data = exerciseFilter === 'mine'
        ? await getExercisesByAuthor(user.uid)
        : await getAllExercisesForTeachers()
      setExercises(data)
    } catch (error) {
      console.error('Erreur lors du chargement des exercices:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleRefreshAllAutoTags = async () => {
    if (!user?.uid || isRefreshingTags) return
    setIsRefreshingTags(true)
    setRefreshTagsMessage(null)
    try {
      const result = await refreshAllAutoTags()
      await loadExercises()
      setRefreshTagsMessage({
        type: 'success',
        text: `${result.updated} exercice(s) mis à jour.${result.failed ? ` ${result.failed} échec(s).` : ''}`
      })
      setTimeout(() => setRefreshTagsMessage(null), 5000)
    } catch (err) {
      setRefreshTagsMessage({ type: 'error', text: err.message || 'Erreur lors de la mise à jour des tags' })
      setTimeout(() => setRefreshTagsMessage(null), 5000)
    } finally {
      setIsRefreshingTags(false)
    }
  }

  // Dérivés pour les filtres
  const availableTags = useMemo(() => {
    const set = new Set()
    exercises.forEach((ex) => {
      const isHorizons = ex.metadata?.section === 'horizons'
      if (horizonsFilter === 'classic' && isHorizons) return
      if (horizonsFilter === 'horizons' && !isHorizons) return
      (ex.autoTags || []).forEach((tag) => set.add(tag))
    })
    return [...set].sort()
  }, [exercises, horizonsFilter])

  const tagCategories = useMemo(() => categorizeTags(availableTags), [availableTags])

  const composers = useMemo(() => {
    const seen = new Set()
    const withFamily = []
    exercises.forEach((ex) => {
      const isHorizons = ex.metadata?.section === 'horizons'
      if (horizonsFilter === 'classic' && isHorizons) return
      if (horizonsFilter === 'horizons' && !isHorizons) return
      const short = getComposerDisplayNameShort(ex.metadata)
      if (!short || seen.has(short)) return
      seen.add(short)
      withFamily.push({ short, familyName: getComposerFamilyName(ex.metadata) })
    })
    return withFamily.sort((a, b) => a.familyName.localeCompare(b.familyName, undefined, { sensitivity: 'base' })).map((x) => x.short)
  }, [exercises, horizonsFilter])

  const authors = useMemo(() => {
    if (exerciseFilter !== 'all') return []
    const byId = new Map()
    exercises.forEach((ex) => {
      const id = ex.authorId || ''
      if (!id) return
      const name = ex.authorId === user?.uid ? 'Vous' : (ex.authorName || 'Professeur')
      if (!byId.has(id)) byId.set(id, { authorId: id, displayName: name })
    })
    return [...byId.values()].sort((a, b) => a.displayName.localeCompare(b.displayName))
  }, [exercises, exerciseFilter, user?.uid])

  const formationIds = useMemo(() => {
    const set = new Set()
    exercises.forEach((ex) => {
      if (ex.metadata?.section === 'horizons') return
      const raw = ex.metadata?.formation
      const arr = Array.isArray(raw) ? raw : raw ? [raw] : inferFormationsFromWorkTitle(ex.metadata?.workTitle || '', null)
      arr.forEach((id) => set.add(id))
    })
    return set
  }, [exercises])

  const genreIds = useMemo(() => {
    const set = new Set()
    exercises.forEach((ex) => {
      if (ex.metadata?.section === 'horizons') return
      const id = ex.metadata?.genre || inferGenreFromWorkTitle(ex.metadata?.workTitle || '')
      if (id) set.add(id)
    })
    return set
  }, [exercises])

  const genreOptions = useMemo(() => CLASSICAL_GENRES.filter((g) => genreIds.has(g.id)), [genreIds])
  const formationCorpusIds = useMemo(() => [...formationIds], [formationIds])

  const homeworkMetaByExerciseId = useMemo(() => {
    const m = new Map()
    if (!homeworkFilterClassId) return m
    const className =
      dashboardTeacherClasses.find((c) => c.id === homeworkFilterClassId)?.name || 'cette classe'
    const truncated = homeworkClassAssignments.length >= ASSIGNMENTS_BY_CLASS_UI_LIMIT
    const latestMs = (raw) => {
      if (!raw) return 0
      const d = raw.toDate ? raw.toDate() : new Date(raw)
      const t = d.getTime()
      return Number.isNaN(t) ? 0 : t
    }
    for (const a of homeworkClassAssignments) {
      const ids = getExerciseIdsFromAssignment(a)
      const t = latestMs(a.createdAt)
      for (const eid of ids) {
        const prev = m.get(eid)
        if (!prev) {
          m.set(eid, { count: 1, latestMs: t, latestRaw: a.createdAt, className, truncated })
        } else {
          prev.count += 1
          if (t > prev.latestMs) {
            prev.latestMs = t
            prev.latestRaw = a.createdAt
          }
        }
      }
    }
    return m
  }, [homeworkFilterClassId, homeworkClassAssignments, dashboardTeacherClasses])

  const homeworkAssignedExerciseIds = useMemo(
    () => new Set(homeworkMetaByExerciseId.keys()),
    [homeworkMetaByExerciseId]
  )

  const filteredExercises = useMemo(() => {
    let list = [...exercises]
    if (horizonsFilter === 'classic') {
      list = list.filter(ex => ex.metadata?.section !== 'horizons')
    } else if (horizonsFilter === 'horizons') {
      list = list.filter(ex => ex.metadata?.section === 'horizons')
    }
    if (debouncedSearchText.trim()) {
      const q = debouncedSearchText.toLowerCase().trim()
      list = list.filter(
        (ex) =>
          (ex.metadata?.workTitle || '').toLowerCase().includes(q) ||
          (ex.metadata?.exerciseTitle || '').toLowerCase().includes(q) ||
          (getComposerDisplayName(ex.metadata) || '').toLowerCase().includes(q)
      )
    }
    if (selectedDifficulty) list = list.filter((ex) => getDisplayDifficulty(ex.metadata?.difficulty) === selectedDifficulty)
    if (selectedComposer) list = list.filter((ex) => getComposerDisplayNameShort(ex.metadata) === selectedComposer)
    if (selectedGenre) {
      list = list.filter((ex) => {
        const g = ex.metadata?.genre || inferGenreFromWorkTitle(ex.metadata?.workTitle || '')
        return g === selectedGenre
      })
    }
    if (selectedFormations.length > 0) {
      list = list.filter((ex) => {
        const raw = ex.metadata?.formation
        const exFormations = Array.isArray(raw) ? raw : raw ? [raw] : inferFormationsFromWorkTitle(ex.metadata?.workTitle || '', null)
        return selectedFormations.every((s) => exFormations.includes(s))
      })
    }
    if (selectedTags.length > 0) {
      list = list.filter((ex) => {
        const tags = ex.autoTags || []
        return tagMatchMode === 'all'
          ? selectedTags.every((tag) => tags.includes(tag))
          : selectedTags.some((tag) => tags.includes(tag))
      })
    }
    if (selectedAuthorId) list = list.filter((ex) => ex.authorId === selectedAuthorId)
    if (selectedStatus !== 'all') list = list.filter((ex) => (ex.status || 'draft') === selectedStatus)
    if (selectedVitrineOnly) list = list.filter((ex) => ex.publicPreview === true)
    if (homeworkFilterClassId && homeworkRestrictOnlyAssigned) {
      list = list.filter((ex) => homeworkAssignedExerciseIds.has(ex.id))
    }
    return list
  }, [
    exercises,
    horizonsFilter,
    debouncedSearchText,
    selectedDifficulty,
    selectedComposer,
    selectedGenre,
    selectedFormations,
    selectedTags,
    tagMatchMode,
    selectedAuthorId,
    selectedStatus,
    selectedVitrineOnly,
    homeworkFilterClassId,
    homeworkRestrictOnlyAssigned,
    homeworkAssignedExerciseIds
  ])

  const sortedExercises = useMemo(() => {
    const list = [...filteredExercises]
    const getDate = (ex) => ex.createdAt?.toDate?.()?.getTime() ?? ex.createdAt ?? 0
    const getWorkTitle = (ex) => (ex.metadata?.workTitle || '').trim()
    const getExerciseTitle = (ex) => (ex.metadata?.exerciseTitle || '').trim()
    const getComposerFamily = (ex) => getComposerFamilyName(ex.metadata).trim()
    list.sort((a, b) => {
      if (sortBy === 'date') {
        const d = getDate(b) - getDate(a)
        if (d !== 0) return d
        return (a.id || '').localeCompare(b.id || '')
      }
      if (sortBy === 'title') {
        const wa = getWorkTitle(a)
        const wb = getWorkTitle(b)
        const emptyA = !wa
        const emptyB = !wb
        if (emptyA && emptyB) {
          const ea = getExerciseTitle(a).toLowerCase()
          const eb = getExerciseTitle(b).toLowerCase()
          const c = ea.localeCompare(eb)
          if (c !== 0) return c
          return getDate(b) - getDate(a) || (a.id || '').localeCompare(b.id || '')
        }
        if (emptyA) return 1
        if (emptyB) return -1
        const c = wa.localeCompare(wb, undefined, { sensitivity: 'base' })
        if (c !== 0) return c
        const c2 = getExerciseTitle(a).localeCompare(getExerciseTitle(b), undefined, { sensitivity: 'base' })
        if (c2 !== 0) return c2
        return getDate(b) - getDate(a) || (a.id || '').localeCompare(b.id || '')
      }
      const ca = getComposerFamily(a)
      const cb = getComposerFamily(b)
      const emptyA = !ca
      const emptyB = !cb
      if (emptyA && emptyB) return getDate(b) - getDate(a) || (a.id || '').localeCompare(b.id || '')
      if (emptyA) return 1
      if (emptyB) return -1
      const c = ca.localeCompare(cb, undefined, { sensitivity: 'base' })
      if (c !== 0) return c
      const c2 = getWorkTitle(a).localeCompare(getWorkTitle(b), undefined, { sensitivity: 'base' })
      if (c2 !== 0) return c2
      return getDate(b) - getDate(a) || (a.id || '').localeCompare(b.id || '')
    })
    return list
  }, [filteredExercises, sortBy])

  const dashboardTotalPages = Math.max(1, Math.ceil(sortedExercises.length / DASHBOARD_EXERCISES_PAGE_SIZE))
  const paginatedExercises = useMemo(() => {
    const start = dashboardResultsPage * DASHBOARD_EXERCISES_PAGE_SIZE
    return sortedExercises.slice(start, start + DASHBOARD_EXERCISES_PAGE_SIZE)
  }, [sortedExercises, dashboardResultsPage])

  useEffect(() => {
    setDashboardResultsPage(0)
  }, [
    debouncedSearchText,
    horizonsFilter,
    selectedDifficulty,
    selectedComposer,
    selectedGenre,
    selectedFormations,
    selectedTags,
    tagMatchMode,
    selectedAuthorId,
    selectedStatus,
    selectedVitrineOnly,
    homeworkFilterClassId,
    homeworkRestrictOnlyAssigned,
    sortBy,
    exerciseFilter
  ])

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(sortedExercises.length / DASHBOARD_EXERCISES_PAGE_SIZE) - 1)
    setDashboardResultsPage((p) => Math.min(p, maxPage))
  }, [sortedExercises.length])

  useEffect(() => {
    if (!paginatedExercises.length) {
      setVideoRanksMap(new Map())
      return
    }
    getVideoRanksForExercises(paginatedExercises).then(setVideoRanksMap).catch(() => setVideoRanksMap(new Map()))
  }, [paginatedExercises])

  useEffect(() => {
    if (skipDashboardPaginationScrollRef.current) {
      skipDashboardPaginationScrollRef.current = false
      return
    }
    dashboardResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [dashboardResultsPage])

  const hasActiveFilters =
    searchText ||
    selectedDifficulty ||
    selectedComposer ||
    selectedTags.length > 0 ||
    selectedGenre ||
    selectedFormations.length > 0 ||
    selectedAuthorId ||
    selectedStatus !== 'all' ||
    selectedVitrineOnly ||
    !!homeworkFilterClassId ||
    homeworkRestrictOnlyAssigned

  const clearAllFilters = useCallback(() => {
    resetListFilters()
    clearScrollRestoration('dashboard')
  }, [resetListFilters])

  const toggleTag = useCallback((tag) => {
    setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]))
  }, [])


  const toggleTagSection = useCallback((key) => {
    setExpandedTagSections((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const handleAddToLivePlaylist = useCallback((exercise) => {
    const classId = liveDraftInfo.selectedClassId
    const title = getExerciseDisplayTitle(exercise, exercises) || exercise.metadata?.exerciseTitle || exercise.metadata?.workTitle || 'Exercice'
    if (!classId) {
      navigate('/dashboard/live-sessions')
      setLiveDraftToast('Choisis d abord une classe dans Sessions live.')
      return
    }
    const result = addExerciseToLiveSessionDraft({
      classId,
      exerciseId: exercise.id,
      exerciseTitle: title
    })
    if (result.duplicate) {
      setLiveDraftToast('Exercice deja present dans la playlist.')
    } else if (result.added) {
      const classLabel = liveDraftInfo.selectedClassLabel || classId
      setLiveDraftToast(`Ajoute a ${classLabel} (${result.queueLength})`)
    }
    refreshLiveDraftInfo()
  }, [liveDraftInfo.selectedClassId, liveDraftInfo.selectedClassLabel, exercises, navigate, refreshLiveDraftInfo])

  const handleDelete = async (exerciseId, authorId, authorName) => {
    const isOwn = authorId === user.uid
    const msg = isOwn
      ? 'Êtes-vous sûr de vouloir supprimer cet exercice ?'
      : `Cet exercice appartient à ${authorName || 'un autre professeur'}. Supprimer quand même ?`
    if (!window.confirm(msg)) {
      return
    }

    try {
      setDeletingId(exerciseId)
      setOpenMenuId(null)
      await deleteExercise(exerciseId)
      await loadExercises()
    } catch (error) {
      console.error('Erreur lors de la suppression:', error)
      alert('Erreur lors de la suppression de l\'exercice')
    } finally {
      setDeletingId(null)
    }
  }

  const handleDuplicate = async (exerciseId) => {
    try {
      setDuplicatingId(exerciseId)
      setOpenMenuId(null)
      const newId = await duplicateExercise(exerciseId, user.uid, user.displayName || user.email)
      await loadExercises()
    } catch (error) {
      console.error('Erreur lors de la duplication:', error)
      alert('Erreur lors de la duplication de l\'exercice')
    } finally {
      setDuplicatingId(null)
    }
  }

  const handleToggleVitrine = useCallback(async (exercise, nextValue) => {
    if (!isAdminTeacher || !exercise?.id) return
    if (exercise.status !== 'published') return
    const previous = !!exercise.publicPreview
    setVitrineTogglingId(exercise.id)
    setExercises((prev) =>
      prev.map((ex) => (ex.id === exercise.id ? { ...ex, publicPreview: nextValue } : ex))
    )
    try {
      await setExercisePublicPreview(exercise.id, nextValue)
    } catch (error) {
      console.error('Erreur vitrine beta:', error)
      setExercises((prev) =>
        prev.map((ex) => (ex.id === exercise.id ? { ...ex, publicPreview: previous } : ex))
      )
      alert('Impossible de mettre à jour la vitrine beta')
    } finally {
      setVitrineTogglingId(null)
    }
  }, [isAdminTeacher])

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date inconnue'
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(date)
  }

  const getYouTubeThumbnail = (videoId) => {
    if (!videoId) return null
    // Extraire l'ID si c'est une URL complète
    const id = videoId.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1] || videoId
    return `https://img.youtube.com/vi/${id}/mqdefault.jpg`
  }

  const toggleCardMenu = (exerciseId, e) => {
    e.stopPropagation()
    setOpenMenuId(openMenuId === exerciseId ? null : exerciseId)
  }

  if (authLoading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-loading">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-empty">
          <div className="dashboard-empty-icon">🔐</div>
          <h2>Connexion requise</h2>
          <p>Connectez-vous pour accéder à vos exercices</p>
          <button 
            className="dashboard-signin-btn"
            onClick={async () => {
              try {
                await signInWithGoogle()
              } catch (error) {
                console.error('Erreur lors de la connexion:', error)
                if (error.code !== 'auth/popup-closed-by-user') {
                  alert(getAuthErrorMessage(error))
                }
              }
            }}
          >
            Se connecter avec Google
          </button>
          <button
            type="button"
            className="dashboard-signin-email-link"
            onClick={() => setShowEmailLoginModal(true)}
          >
            Se connecter avec email
          </button>
        </div>
        <EmailLoginModal isOpen={showEmailLoginModal} onClose={() => setShowEmailLoginModal(false)} />
      </div>
    )
  }

  return (
    <div className="dashboard-container">
      {/* Header simplifié et sticky */}
      <header className="dashboard-header">
          <h1 className="dashboard-title">Mes Exercices</h1>
        <div className="dashboard-header-right" ref={userMenuRef}>
          {userData?.role === 'teacher' && (
            <button
              type="button"
              className="dashboard-header-student-btn"
              onClick={() => navigate('/student-dashboard')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
              <span>Voir interface élève</span>
            </button>
          )}
          <button
            className="dashboard-user-avatar-btn"
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            aria-label="Menu utilisateur"
          >
            {(userData?.photoURL || user?.photoURL) ? (
              <img 
                src={userData?.photoURL || user?.photoURL} 
                alt={user.displayName || 'Utilisateur'} 
                className="dashboard-user-avatar"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="dashboard-user-avatar-placeholder">
                {user?.displayName?.[0]?.toUpperCase() || 'U'}
              </div>
            )}
          </button>
          
          {/* Menu dropdown utilisateur */}
          {isUserMenuOpen && (
            <div className="dashboard-user-menu">
              <div className="dashboard-user-menu-header">
                <div className="dashboard-user-menu-name">
                  {userData?.displayName || user?.displayName || 'Utilisateur'}
                </div>
                <div className="dashboard-user-menu-role">
                  {userData?.role === 'teacher' ? 'Professeur' : 'Élève'}
            </div>
          </div>
              <div className="dashboard-user-menu-divider"></div>
              <button 
                className="dashboard-user-menu-item"
                onClick={() => {
                  setIsProfileModalOpen(true)
                  setIsUserMenuOpen(false)
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span>Profil et statistiques</span>
              </button>
              {userData?.role === 'teacher' && (
              <>
              <button 
                  className="dashboard-user-menu-item"
                  onClick={() => {
                    navigate('/dashboard/students')
                    setIsUserMenuOpen(false)
                  }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <span>Catalogue élèves</span>
                {pendingRequestsCount > 0 && (
                  <span className="dashboard-user-menu-badge">{pendingRequestsCount}</span>
                )}
              </button>
              <button 
                  className="dashboard-user-menu-item"
                  onClick={() => {
                    navigate('/dashboard/classes')
                    setIsUserMenuOpen(false)
                  }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                  <line x1="8" y1="6" x2="16" y2="6"></line>
                  <line x1="8" y1="10" x2="16" y2="10"></line>
                </svg>
                <span>Mes classes</span>
              </button>
              <button 
                  className="dashboard-user-menu-item"
                  onClick={() => {
                    navigate('/dashboard/assignments')
                    setIsUserMenuOpen(false)
                  }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                <span>Devoirs</span>
              </button>
              <button
                  className="dashboard-user-menu-item"
                  onClick={() => {
                    navigate('/dashboard/live-sessions')
                    setIsUserMenuOpen(false)
                  }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 12h18"></path>
                  <path d="M12 3v18"></path>
                  <circle cx="12" cy="12" r="9"></circle>
                </svg>
                <span>Sessions live</span>
              </button>
              <button
                  className="dashboard-user-menu-item"
                  onClick={() => {
                    navigate('/dashboard/insights')
                    setIsUserMenuOpen(false)
                  }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18"></path>
                  <path d="M7 15l4-4 3 3 5-6"></path>
                </svg>
                <span>Insights prof</span>
              </button>
              <button 
                  className="dashboard-user-menu-item"
                  onClick={() => {
                    navigate('/dashboard/teachers')
                    setIsUserMenuOpen(false)
                  }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 14l9-5-9-5-9 5 9 5z"></path>
                  <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"></path>
                </svg>
                <span>Annuaire des professeurs</span>
              </button>
              {canAccessParcoursImagesEditor(user?.email) && (
              <button
                  className="dashboard-user-menu-item"
                  onClick={() => {
                    navigate('/dashboard/parcours-images')
                    setIsUserMenuOpen(false)
                  }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
                <span>Images parcours / horizons</span>
              </button>
              )}
              {isAdminTeacher && (
              <button
                  className="dashboard-user-menu-item"
                  onClick={() => {
                    navigate('/dashboard/codex-admin')
                    setIsUserMenuOpen(false)
                  }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M2 4h20v16H2z"></path>
                  <path d="M6 8h12"></path>
                  <path d="M6 12h12"></path>
                  <path d="M6 16h8"></path>
                </svg>
                <span>Codex admin</span>
              </button>
              )}
              <button
                  className="dashboard-user-menu-item"
                  onClick={() => {
                    navigate('/dashboard/parcours-coverage')
                    setIsUserMenuOpen(false)
                  }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 1 1-9-9"></path>
                  <path d="M21 3v6h-6"></path>
                  <path d="M9 9l3 3 8-8"></path>
                </svg>
                <span>Couverture parcours</span>
              </button>
              <button 
                  className="dashboard-user-menu-item"
                  onClick={() => {
                    navigate('/student-dashboard')
                    setIsUserMenuOpen(false)
                  }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <span>Voir interface élève</span>
              </button>
              </>
          )}
              <div className="dashboard-user-menu-divider"></div>
          <button 
                className="dashboard-user-menu-item dashboard-user-menu-item-danger"
            onClick={async () => {
              try {
                await logout()
                navigate('/')
              } catch (error) {
                console.error('Erreur lors de la déconnexion:', error)
              }
                  setIsUserMenuOpen(false)
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
                <span>Déconnexion</span>
          </button>
            </div>
          )}
        </div>
      </header>

      {/* Filtre Mes exercices / Tous les exercices */}
      <div className="dashboard-filter-tabs">
        <button
          type="button"
          className={`dashboard-filter-tab ${exerciseFilter === 'mine' ? 'active' : ''}`}
          onClick={() => setExerciseFilter('mine')}
        >
          Mes exercices
        </button>
        <button
          type="button"
          className={`dashboard-filter-tab ${exerciseFilter === 'all' ? 'active' : ''}`}
          onClick={() => setExerciseFilter('all')}
        >
          Tous les exercices
        </button>
        <button
          type="button"
          className="dashboard-filter-tab"
          onClick={() => navigate('/dashboard/live-sessions')}
          title="Ouvrir les sessions live classe"
        >
          Sessions live
        </button>
        <span className="dashboard-filter-tabs-separator" />
        <div className="dashboard-horizons-segmented">
          <button
            type="button"
            className={`dashboard-horizons-seg ${horizonsFilter === 'classic' ? 'active' : ''}`}
            onClick={() => setHorizonsFilter('classic')}
            title="Exercices classiques uniquement"
          >
            Classique
          </button>
          <button
            type="button"
            className={`dashboard-horizons-seg ${horizonsFilter === 'all' ? 'active' : ''}`}
            onClick={() => setHorizonsFilter('all')}
            title="Tous les exercices (classiques + Nouveaux Horizons)"
          >
            Tous
          </button>
          <button
            type="button"
            className={`dashboard-horizons-seg horizons ${horizonsFilter === 'horizons' ? 'active' : ''}`}
            onClick={() => setHorizonsFilter('horizons')}
            title="Exercices Nouveaux Horizons uniquement"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="2" y1="12" x2="22" y2="12"></line>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
            </svg>
            Horizons
          </button>
        </div>
      </div>
      <div className="dashboard-live-draft-bar">
        {liveDraftInfo.selectedClassId ? (
          <>
            <span>
              Playlist live: <strong>{liveDraftInfo.selectedClassLabel || liveDraftInfo.selectedClassId}</strong> - {liveDraftInfo.queueCount} exercice{liveDraftInfo.queueCount > 1 ? 's' : ''}
            </span>
            <button type="button" onClick={() => navigate('/dashboard/live-sessions')}>Ouvrir sessions live</button>
          </>
        ) : (
          <>
            <span>Aucune classe live sélectionnée pour la playlist.</span>
            <button type="button" onClick={() => navigate('/dashboard/live-sessions')}>Choisir une classe</button>
          </>
        )}
      </div>
      {liveDraftToast ? (
        <div className="dashboard-live-draft-toast" role="status">{liveDraftToast}</div>
      ) : null}

      {/* Barre de recherche + bouton Filtres (mobile) */}
      <div className="dashboard-search-row">
        <label
          className={`dashboard-search-input-wrapper${searchText ? ' dashboard-search-input-wrapper--has-value' : ''}`}
          htmlFor="dashboard-search-input"
        >
          <svg className="dashboard-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            id="dashboard-search-input"
            type="search"
            className="dashboard-search-input"
            placeholder={isMobileLayout ? 'Rechercher…' : 'Rechercher (titre, compositeur, œuvre…)'}
            aria-label="Rechercher (titre, compositeur, œuvre…)"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {searchText && (
            <button type="button" className="dashboard-search-clear" onClick={() => setSearchText('')} aria-label="Effacer">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
        </label>
        {isAdminTeacher && userData?.role === 'teacher' && (
          <button
            type="button"
            className="dashboard-filter-btn dashboard-refresh-tags-btn"
            onClick={handleRefreshAllAutoTags}
            disabled={isRefreshingTags}
            aria-label="Mettre à jour les tags"
            title="Recalcule les tags automatiques (accords, fonctions, cadences…) pour tous les exercices de la plateforme. Réservé aux profs pouvant éditer tous les exercices."
          >
            {isRefreshingTags ? (
              <span className="dashboard-refresh-tags-spinner" aria-hidden />
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16" />
              </svg>
            )}
            <span className="dashboard-filter-btn-label">{isRefreshingTags ? 'Mise à jour…' : 'tags'}</span>
          </button>
        )}
        <button
          type="button"
          className="dashboard-filter-btn"
          onClick={() => setIsFilterDrawerOpen(true)}
          aria-label="Filtrer"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
          </svg>
          <span className="dashboard-filter-btn-label">Filtrer</span>
          {hasActiveFilters && (
            <span className="dashboard-filter-badge">
              {(searchText ? 1 : 0) +
                (selectedDifficulty ? 1 : 0) +
                (selectedComposer ? 1 : 0) +
                (selectedTags.length ? 1 : 0) +
                (selectedGenre ? 1 : 0) +
                (selectedFormations.length ? 1 : 0) +
                (selectedAuthorId ? 1 : 0) +
                (selectedStatus !== 'all' ? 1 : 0)}
            </span>
          )}
        </button>
      </div>

      {refreshTagsMessage && (
        <div className={`dashboard-refresh-tags-message dashboard-refresh-tags-message--${refreshTagsMessage.type}`} role="status">
          {asDisplayText(refreshTagsMessage, '')}
        </div>
      )}

      {/* Bloc Crédits API */}
      {canViewApiUsage(user?.email, user?.uid) && isOpusApiConfigured() && (
        <section className="dashboard-api-usage" aria-label="Crédits API">
          {apiUsageLoading ? <p className="dashboard-api-usage-loading">Chargement des crédits…</p> : !apiUsage?.available ? <p className="dashboard-api-usage-unavailable">Comptage des crédits non disponible. Vérifiez la configuration du Worker (KV).</p> : (
            <div className="dashboard-api-usage-content">
              <div className="dashboard-api-usage-row"><span>Recherches YouTube :</span> <strong>{apiUsage.youtube ?? 0} / {apiUsage.limits?.youtube ?? 0}</strong></div>
              <div className="dashboard-api-usage-row"><span>Parse titre (Gemini) :</span> <strong>{apiUsage.gemini ?? 0} / {apiUsage.limits?.gemini ?? 0}</strong></div>
              <p className="dashboard-api-usage-exercises">Environ <strong>{apiUsage.estimatedExercisesLeft ?? 0}</strong> exercices encore possibles aujourd&apos;hui.</p>
              <button type="button" className="dashboard-api-usage-refresh" onClick={loadApiUsage} disabled={apiUsageLoading}>Rafraîchir</button>
            </div>
          )}
        </section>
      )}
      {/* Contenu principal : sidebar filtres + grille */}
      <main className="dashboard-content dashboard-content-with-filters">
        {/* Panneau filtres (desktop) */}
        <aside className="dashboard-filters">
          <div className="dashboard-filters-header">
            <h3>Filtres</h3>
            <div className="dashboard-filters-header-actions">
              <div className="dashboard-tag-match-segmented" role="group" aria-label="Correspondance tags">
                <button
                  type="button"
                  className={`dashboard-tag-match-seg ${tagMatchMode === 'any' ? 'active' : ''}`}
                  onClick={() => setTagMatchMode('any')}
                  title="Afficher les exercices qui correspondent à au moins un tag sélectionné"
                >
                  OU
                </button>
                <button
                  type="button"
                  className={`dashboard-tag-match-seg ${tagMatchMode === 'all' ? 'active' : ''}`}
                  onClick={() => setTagMatchMode('all')}
                  title="Afficher seulement les exercices qui contiennent tous les tags sélectionnés"
                >
                  ET
                </button>
              </div>
              {hasActiveFilters && (
                <button type="button" className="dashboard-clear-filters" onClick={clearAllFilters}>
                  Réinitialiser
                </button>
              )}
            </div>
          </div>

          {/* Statut publication (prof) */}
          <div className="dashboard-filter-block">
            <h4 className="dashboard-filter-block-title">Statut</h4>
            <div className="dashboard-statut-chips">
              <button
                type="button"
                className={`dashboard-statut-chip ${selectedStatus === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedStatus('all')}
              >
                Tous
              </button>
              <button
                type="button"
                className={`dashboard-statut-chip ${selectedStatus === 'published' ? 'active' : ''}`}
                onClick={() => setSelectedStatus('published')}
              >
                Publié
              </button>
              <button
                type="button"
                className={`dashboard-statut-chip ${selectedStatus === 'draft' ? 'active' : ''}`}
                onClick={() => setSelectedStatus('draft')}
              >
                Brouillon
              </button>
              {isAdminTeacher && (
                <button
                  type="button"
                  className={`dashboard-statut-chip ${selectedVitrineOnly ? 'active' : ''}`}
                  onClick={() => setSelectedVitrineOnly((v) => !v)}
                  aria-pressed={selectedVitrineOnly}
                  title="Afficher uniquement les exercices vitrine beta"
                >
                  Vitrine
                </button>
              )}
            </div>
          </div>

          {userData?.role === 'teacher' && (
            <div className="dashboard-filter-block">
              <h4 className="dashboard-filter-block-title">Devoirs (par classe)</h4>
              <label className="dashboard-homework-filter-label" htmlFor="dashboard-homework-class-select">
                Classe
              </label>
              <select
                id="dashboard-homework-class-select"
                className="dashboard-homework-class-select"
                value={homeworkFilterClassId}
                onChange={(e) => {
                  const v = e.target.value
                  setHomeworkFilterClassId(v)
                  if (!v) setHomeworkRestrictOnlyAssigned(false)
                }}
                aria-label="Filtrer ou marquer les exercices selon les devoirs d'une classe"
              >
                <option value="">Indifférent</option>
                {dashboardTeacherClasses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {homeworkFilterClassId && (
                <>
                  {homeworkAssignmentsLoading && (
                    <p className="dashboard-homework-filter-hint" role="status">
                      Chargement des devoirs…
                    </p>
                  )}
                  {!homeworkAssignmentsLoading && homeworkClassAssignments.length >= ASSIGNMENTS_BY_CLASS_UI_LIMIT && (
                    <p className="dashboard-homework-filter-hint dashboard-homework-filter-hint--warn" role="note">
                      Affichage limité aux {ASSIGNMENTS_BY_CLASS_UI_LIMIT} devoirs les plus récents pour cette classe.
                    </p>
                  )}
                  <label className="dashboard-homework-restrict-label">
                    <input
                      type="checkbox"
                      checked={homeworkRestrictOnlyAssigned}
                      onChange={(e) => setHomeworkRestrictOnlyAssigned(e.target.checked)}
                    />
                    <span>N&apos;afficher que les exercices déjà donnés à cette classe</span>
                  </label>
                </>
              )}
            </div>
          )}

          {/* Créateur (uniquement pour "Tous les exercices") */}
          {exerciseFilter === 'all' && authors.length > 0 && (
            <div className="dashboard-filter-block">
              <h4 className="dashboard-filter-block-title">Créateur</h4>
              <div className="dashboard-chips-container">
                <button
                  type="button"
                  className={`dashboard-chip ${selectedAuthorId === '' ? 'active' : ''}`}
                  onClick={() => setSelectedAuthorId('')}
                >
                  Tous
                </button>
                {authors.map((a) => (
                  <button
                    key={a.authorId}
                    type="button"
                    className={`dashboard-chip ${selectedAuthorId === a.authorId ? 'active' : ''}`}
                    onClick={() => setSelectedAuthorId(selectedAuthorId === a.authorId ? '' : a.authorId)}
                  >
                    {a.displayName}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Niveau */}
          <div className="dashboard-filter-block">
            <h4 className="dashboard-filter-block-title">Niveau</h4>
            <div className="dashboard-difficulty-cards">
              {DIFFICULTIES.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`dashboard-difficulty-card ${selectedDifficulty === d ? 'active' : ''}`}
                  onClick={() => setSelectedDifficulty(selectedDifficulty === d ? '' : d)}
                >
                  <span className="dashboard-difficulty-icon">
                    {d === 'facile' && '🌱'}
                    {d === 'intermédiaire' && '⭐'}
                    {d === 'difficile' && '🔥'}
                    {d === 'expert' && '💎'}
                  </span>
                  <span className="dashboard-difficulty-label">{d}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Compositeur */}
          {composers.length > 0 && (
            <div className="dashboard-filter-block">
              <h4 className="dashboard-filter-block-title">Compositeur</h4>
              <div className="dashboard-chips-container">
                <button type="button" className={`dashboard-chip ${selectedComposer === '' ? 'active' : ''}`} onClick={() => setSelectedComposer('')}>
                  Tous
                </button>
                {(composersExpanded ? composers : composers.slice(0, COMPOSERS_VISIBLE_INITIAL)).map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`dashboard-chip ${selectedComposer === c ? 'active' : ''}`}
                    onClick={() => setSelectedComposer(selectedComposer === c ? '' : c)}
                  >
                    {c}
                  </button>
                ))}
                {composers.length > COMPOSERS_VISIBLE_INITIAL && !composersExpanded && (
                  <button type="button" className="dashboard-chip dashboard-chip-load-more" onClick={() => setComposersExpanded(true)}>
                    Charger plus ({composers.length - COMPOSERS_VISIBLE_INITIAL})
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Genre */}
          {genreOptions.length > 0 && (
            <div className="dashboard-filter-block">
              <h4 className="dashboard-filter-block-title">Genre (type d&apos;œuvre)</h4>
              <div className="dashboard-chips-container">
                <button type="button" className={`dashboard-chip ${selectedGenre === '' ? 'active' : ''}`} onClick={() => setSelectedGenre('')}>
                  Tous
                </button>
                {genreOptions.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    className={`dashboard-chip ${selectedGenre === g.id ? 'active' : ''}`}
                    onClick={() => setSelectedGenre(selectedGenre === g.id ? '' : g.id)}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Formation */}
          <div className="dashboard-filter-block">
            <h4 className="dashboard-filter-block-title">Formation (instrumentation)</h4>
            <div className="dashboard-chips-container">
              <button type="button" className={`dashboard-chip ${selectedFormations.length === 0 ? 'active' : ''}`} onClick={() => setSelectedFormations([])}>
                Toutes
              </button>
            </div>
            <FormationInstrumentPicker
              masterList={CLASSICAL_FORMATIONS}
              value={selectedFormations}
              onChange={setSelectedFormations}
              prioritizedIds={formationCorpusIds}
              theme="dashboard"
              showClearAll={false}
              hint="Instruments déjà présents dans tes exercices en premier — tape pour la liste complète."
            />
          </div>

          {/* Tags : accordéon avec cases à cocher */}
          {TAG_CATEGORY_KEYS.map((key) => {
            const tagsInCategory = tagCategories[key] || []
            if (tagsInCategory.length === 0) return null
            const isExpanded = expandedTagSections.has(key)
            const label = TAG_CATEGORY_LABELS[key]
            return (
              <div key={key} className="dashboard-filter-block dashboard-filter-block-tags">
                <button
                  type="button"
                  className="dashboard-filter-accordion-header"
                  onClick={() => toggleTagSection(key)}
                  aria-expanded={isExpanded}
                >
                  <span>{label}</span>
                  <span className="dashboard-filter-accordion-icon">{isExpanded ? '▼' : '▶'}</span>
                </button>
                {isExpanded && (
                  <div className="dashboard-filter-tag-checkboxes">
                    {tagsInCategory.map((tag) => (
                      <label key={tag} className="dashboard-filter-tag-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedTags.includes(tag)}
                          onChange={() => toggleTag(tag)}
                        />
                        <span>{formatTagForDisplay(tag)}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </aside>

        {/* Zone résultats */}
        <div className="dashboard-results" ref={dashboardResultsRef}>
          {loading ? (
            <div className="dashboard-loading">
              <div className="spinner"></div>
              <p>Chargement des exercices...</p>
            </div>
          ) : exercises.length === 0 ? (
            <div className="dashboard-empty">
              <div className="dashboard-empty-icon">📚</div>
              <h2>{exerciseFilter === 'mine' ? 'Aucun exercice pour le moment' : 'Aucun exercice'}</h2>
              <p>{exerciseFilter === 'mine' ? "Créez votre premier exercice d'analyse harmonique" : 'Aucun exercice créé par les professeurs.'}</p>
            </div>
          ) : filteredExercises.length === 0 ? (
            <div className="dashboard-empty">
              <div className="dashboard-empty-icon">🔍</div>
              <h2>Aucun exercice trouvé</h2>
              <p>Modifiez les critères de recherche ou réinitialisez les filtres.</p>
              {hasActiveFilters && (
                <button type="button" className="dashboard-empty-cta" onClick={clearAllFilters}>
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="dashboard-results-row">
                <p className="dashboard-results-count">
                  {sortedExercises.length} exercice{sortedExercises.length !== 1 ? 's' : ''} trouvé{sortedExercises.length !== 1 ? 's' : ''}
                  {sortedExercises.length > DASHBOARD_EXERCISES_PAGE_SIZE && (
                    <span className="dashboard-results-range">
                      {' '}
                      · affichés {dashboardResultsPage * DASHBOARD_EXERCISES_PAGE_SIZE + 1}–
                      {Math.min((dashboardResultsPage + 1) * DASHBOARD_EXERCISES_PAGE_SIZE, sortedExercises.length)}
                    </span>
                  )}
                </p>
                <div className="dashboard-sort">
                  <label htmlFor="dashboard-sort-select" className="dashboard-sort-label">Trier par</label>
                  <select
                    id="dashboard-sort-select"
                    className="dashboard-sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    aria-label="Trier par"
                  >
                    <option value="date">Derniers ajoutés</option>
                    <option value="title">Titre (A→Z)</option>
                    <option value="composer">Compositeur (A→Z)</option>
                  </select>
                </div>
              </div>
              <div className="dashboard-grid">
                {paginatedExercises.map((exercise) => {
              const videoId = exercise.video?.id
              const thumbnailUrl = exercise.thumbnailUrl || getYouTubeThumbnail(videoId)
              const isMenuOpen = openMenuId === exercise.id
              const videoRankInfo = videoRanksMap.get(exercise.id)
              const showIndexBadge = videoRankInfo && videoRankInfo.groupSize >= 2
              const groupRank = videoRankInfo?.rank ?? 1
              const groupSize = videoRankInfo?.groupSize ?? 1
              const canEditThis = exercise.authorId === user.uid || isAdminTeacher
              const hwMeta = homeworkMetaByExerciseId.get(exercise.id)
              const homeworkCardTitle =
                homeworkFilterClassId && hwMeta
                  ? `Déjà donné à ${hwMeta.className} — ${hwMeta.count} devoir(s)${
                      hwMeta.latestRaw ? ` — dernier : ${formatDate(hwMeta.latestRaw)}` : ''
                    }${hwMeta.truncated ? ' (liste des devoirs tronquée)' : ''}`
                  : undefined
              return (
            <div 
              key={exercise.id} 
              className={`dashboard-card${isMenuOpen ? ' dashboard-card--menu-open' : ''}${homeworkFilterClassId && hwMeta ? ' dashboard-card--homework-marked' : ''}`}
              title={homeworkCardTitle || undefined}
              onClick={() => canEditThis
                ? navigate(`/editor/${exercise.id}`, { state: { returnTo: dashboardReturnTo } })
                : navigate(`/play/${exercise.id}`, { state: { returnTo: dashboardReturnTo } })
              }
            >
                  {/* Miniature vidéo ou placeholder */}
                  <div className="dashboard-card-thumbnail">
                    {homeworkFilterClassId && hwMeta && (
                      <span className="dashboard-card-homework-badge">Devoir</span>
                    )}
                    {exercise.publicPreview === true && (
                      <span
                        className="dashboard-card-vitrine-badge"
                        title="Exercice vitrine (beta)"
                        aria-label="Exercice vitrine beta"
                      >
                        Vitrine
                      </span>
                    )}
                    {showIndexBadge && (
                      <span
                        className="dashboard-card-index-badge"
                        title={`Exercice ${groupRank} sur ${groupSize} (même vidéo)`}
                        aria-label={`Exercice ${groupRank} sur ${groupSize}`}
                      >
                        {groupRank}/{groupSize}
                      </span>
                    )}
                    {thumbnailUrl ? (
                      <img 
                        src={thumbnailUrl} 
                        alt={getExerciseDisplayTitle(exercise, exercises) || exercise.video?.title || 'Vidéo'}
                        className="dashboard-card-thumbnail-img"
                        onError={(e) => {
                          e.target.style.display = 'none'
                          e.target.nextElementSibling.style.display = 'flex'
                        }}
                      />
                    ) : null}
                    <div 
                      className="dashboard-card-thumbnail-placeholder"
                      style={{ display: thumbnailUrl ? 'none' : 'flex' }}
                    >
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M9 18V5l12-2v13"></path>
                        <circle cx="6" cy="18" r="3"></circle>
                        <circle cx="18" cy="16" r="3"></circle>
                      </svg>
              </div>

                    {/* Actions overlay : prévisualisation (œil) à gauche, badge statut à droite */}
                    <div className="dashboard-card-thumbnail-actions">
                      {userData?.role === 'teacher' && (
                        <button
                          type="button"
                          className="dashboard-card-preview-btn dashboard-card-stats-btn"
                          onClick={(e) => {
                            e.stopPropagation()
                            setStatsExercise(exercise)
                          }}
                          aria-label="Voir les statistiques de l'exercice"
                          title="Voir les statistiques de l'exercice"
                        >
                          <BarChart3 className="dashboard-card-preview-icon" size={18} strokeWidth={2} />
                        </button>
                      )}
                      <button
                        type="button"
                        className="dashboard-card-preview-btn"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/play/${exercise.id}`, { state: { returnTo: dashboardReturnTo, teacherPreview: true } })
                        }}
                        aria-label="Voir en mode élève"
                        title="Voir en mode élève"
                      >
                        <Eye className="dashboard-card-preview-icon" size={18} strokeWidth={2} />
                      </button>
                      <div className={`dashboard-card-status-badge ${exercise.status || 'draft'}`}>
                        {exercise.status === 'published' ? 'Publié' : 'Brouillon'}
                      </div>
                    </div>
                  </div>

                  {/* Contenu de la carte */}
                  <div className="dashboard-card-body">
                    <div className="dashboard-card-header">
                      <h3 className="dashboard-card-title">
                        {getExerciseDisplayTitle(exercise, exercises)}
                      </h3>
                      <button
                        className="dashboard-card-menu-btn"
                        onClick={(e) => toggleCardMenu(exercise.id, e)}
                        aria-label="Options"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="1"></circle>
                          <circle cx="12" cy="5" r="1"></circle>
                          <circle cx="12" cy="19" r="1"></circle>
                        </svg>
                      </button>
                      
                      {/* Menu contextuel */}
                      {isMenuOpen && (() => {
                        const canEdit = exercise.authorId === user.uid || isAdminTeacher
                        return (
                        <div 
                          className="dashboard-card-menu"
                          ref={(el) => menuRefs.current[exercise.id] = el}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {canEdit && (
                          <button
                            className="dashboard-card-menu-item"
                            onClick={() => navigate(`/editor/${exercise.id}`, { state: { returnTo: dashboardReturnTo } })}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                            <span>Éditer</span>
                          </button>
                          )}
                          {canEdit && (
                          <button
                            className="dashboard-card-menu-item"
                            onClick={() => {
                              setOpenMenuId(null)
                              setEditTagsExercise(exercise)
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                              <line x1="7" y1="7" x2="7.01" y2="7"></line>
                            </svg>
                            <span>Éditer les tags</span>
                          </button>
                          )}
                          <button
                            className="dashboard-card-menu-item"
                            onClick={() => handleDuplicate(exercise.id)}
                            disabled={duplicatingId === exercise.id}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                            <span>{duplicatingId === exercise.id ? 'Duplication...' : 'Dupliquer'}</span>
                          </button>
                          <button
                            className="dashboard-card-menu-item"
                            onClick={() => {
                              setAssignExercise(exercise)
                              setOpenMenuId(null)
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                              <line x1="8" y1="6" x2="16" y2="6"></line>
                              <line x1="8" y1="10" x2="16" y2="10"></line>
                            </svg>
                            <span>Assigner à une classe</span>
                          </button>
                          <button
                            className="dashboard-card-menu-item"
                            onClick={() => {
                              handleAddToLivePlaylist(exercise)
                              setOpenMenuId(null)
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M8 5v14l11-7z"></path>
                              <path d="M3 5h2"></path>
                              <path d="M3 12h2"></path>
                              <path d="M3 19h2"></path>
                            </svg>
                            <span>
                              {liveDraftInfo.selectedClassId
                                ? `Ajouter a playlist live (${liveDraftInfo.selectedClassLabel || liveDraftInfo.selectedClassId})`
                                : 'Choisir classe live puis ajouter'}
                            </span>
                          </button>
                          {isAdminTeacher && (
                            <label
                              className={`dashboard-card-menu-item dashboard-card-menu-vitrine${exercise.status !== 'published' ? ' is-disabled' : ''}`}
                              title={
                                exercise.status !== 'published'
                                  ? 'Publier l’exercice avant de le mettre en vitrine'
                                  : 'Visible sans compte autorisé lorsque la beta privée est active'
                              }
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={!!exercise.publicPreview}
                                disabled={
                                  exercise.status !== 'published' || vitrineTogglingId === exercise.id
                                }
                                onChange={(e) => {
                                  handleToggleVitrine(exercise, e.target.checked)
                                }}
                              />
                              <span>Vitrine beta</span>
                            </label>
                          )}
                          {canEdit && (
                          <button
                            className="dashboard-card-menu-item dashboard-card-menu-item-danger"
                            onClick={() => handleDelete(exercise.id, exercise.authorId, exercise.authorName)}
                            disabled={deletingId === exercise.id}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                            <span>{deletingId === exercise.id ? 'Suppression...' : 'Supprimer'}</span>
                          </button>
                          )}
                  </div>
                        )
                      })()}
                  </div>

                    {(() => {
                      const sub = getExerciseSubtitle(exercise, exercises)
                      return sub ? <p className="dashboard-card-subtitle">{sub}</p> : null
                    })()}
                    {(() => {
                      const contextLinks = getCodexContextLinksForExercise(exercise)
                      const otherLinks = contextLinks.filter((l) => l.type !== 'composer')
                      const composerDisplay = getComposerDisplayNameShort(exercise.metadata)
                      const hasComposer = Boolean(composerDisplay)
                      if (!hasComposer && otherLinks.length === 0) return null
                      return (
                        <div className="dashboard-card-context-links" onClick={(e) => e.stopPropagation()}>
                          {hasComposer && (
                            <button
                              type="button"
                              className="dashboard-card-composer-link"
                              onClick={() => navigate(getCodexUrlForEntry('note-compositeurs'))}
                            >
                              {composerDisplay}
                            </button>
                          )}
                          {otherLinks.length > 0 && (
                            <span className="dashboard-card-context-book-row">
                              <span className="dashboard-card-context-icon" aria-hidden="true">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                                </svg>
                              </span>
                              {otherLinks.map((link, idx) => (
                                <button
                                  key={`ctx-${link.codexEntryId}-${link.sectionId ?? ''}-${idx}`}
                                  type="button"
                                  className="dashboard-card-context-link"
                                  onClick={() => navigate(getCodexUrlForEntry(link.codexEntryId, link.sectionId))}
                                >
                                  {link.label}
                                </button>
                              ))}
                            </span>
                          )}
                        </div>
                      )
                    })()}
                    {(exerciseFilter === 'all' && (exercise.authorName || exercise.authorId)) && (
                      <p className="dashboard-card-author">
                        Par {exercise.authorId === user.uid ? 'vous' : (exercise.authorName || 'Professeur')}
                      </p>
                    )}

                    <DashboardCardTooltipZone
                      exercise={exercise}
                      exercises={exercises}
                      formatDate={formatDate}
                      selectedDifficulty={selectedDifficulty}
                      selectedTags={selectedTags}
                      onDifficultyFilter={(difficultyLabel) => {
                        setSelectedDifficulty((prev) => (prev === difficultyLabel ? '' : difficultyLabel))
                      }}
                      onTagFilter={toggleTag}
                    />

                    {/* Footer avec métadonnées */}
                    <div className="dashboard-card-footer">
                      <div className="dashboard-card-meta">
                        <span className="dashboard-card-meta-item">
                          {exercise.markers?.length || 0} marqueur{exercise.markers?.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <span className="dashboard-card-date">
                        {formatDate(exercise.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
              </div>
              {sortedExercises.length > DASHBOARD_EXERCISES_PAGE_SIZE && (
                <nav className="dashboard-pagination" aria-label="Pagination des résultats">
                  <button
                    type="button"
                    className="dashboard-pagination-btn"
                    disabled={dashboardResultsPage <= 0}
                    onClick={() => setDashboardResultsPage((p) => Math.max(0, p - 1))}
                  >
                    Précédent
                  </button>
                  <span className="dashboard-pagination-status">
                    Page {dashboardResultsPage + 1} / {dashboardTotalPages}
                  </span>
                  <button
                    type="button"
                    className="dashboard-pagination-btn"
                    disabled={dashboardResultsPage >= dashboardTotalPages - 1}
                    onClick={() => setDashboardResultsPage((p) => Math.min(dashboardTotalPages - 1, p + 1))}
                  >
                    Suivant
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </main>

      {/* Drawer filtres (mobile) */}
      {isFilterDrawerOpen && (
        <>
          <div className="dashboard-filter-drawer-overlay" {...filterDrawerBackdropDismiss} aria-hidden />
          <div className="dashboard-filter-drawer">
            <div className="dashboard-filter-drawer-header">
              <h3>Filtres</h3>
              <button type="button" className="dashboard-filter-drawer-close" onClick={() => setIsFilterDrawerOpen(false)} aria-label="Fermer">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="dashboard-filter-drawer-content">
              <div className="dashboard-sort dashboard-sort--drawer">
                <label htmlFor="dashboard-sort-select-drawer" className="dashboard-sort-label">Trier par</label>
                <select
                  id="dashboard-sort-select-drawer"
                  className="dashboard-sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="Trier par"
                >
                  <option value="date">Derniers ajoutés</option>
                  <option value="title">Titre (A→Z)</option>
                  <option value="composer">Compositeur (A→Z)</option>
                </select>
              </div>
              <div className="dashboard-filter-block">
                <h4 className="dashboard-filter-block-title">Statut</h4>
                <div className="dashboard-statut-chips">
                  <button type="button" className={`dashboard-statut-chip ${selectedStatus === 'all' ? 'active' : ''}`} onClick={() => setSelectedStatus('all')}>Tous</button>
                  <button type="button" className={`dashboard-statut-chip ${selectedStatus === 'published' ? 'active' : ''}`} onClick={() => setSelectedStatus('published')}>Publié</button>
                  <button type="button" className={`dashboard-statut-chip ${selectedStatus === 'draft' ? 'active' : ''}`} onClick={() => setSelectedStatus('draft')}>Brouillon</button>
                </div>
              </div>
              {userData?.role === 'teacher' && (
                <div className="dashboard-filter-block">
                  <h4 className="dashboard-filter-block-title">Devoirs (par classe)</h4>
                  <label className="dashboard-homework-filter-label" htmlFor="dashboard-homework-class-select-drawer">
                    Classe
                  </label>
                  <select
                    id="dashboard-homework-class-select-drawer"
                    className="dashboard-homework-class-select"
                    value={homeworkFilterClassId}
                    onChange={(e) => {
                      const v = e.target.value
                      setHomeworkFilterClassId(v)
                      if (!v) setHomeworkRestrictOnlyAssigned(false)
                    }}
                    aria-label="Filtrer ou marquer les exercices selon les devoirs d'une classe"
                  >
                    <option value="">Indifférent</option>
                    {dashboardTeacherClasses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {homeworkFilterClassId && (
                    <>
                      {homeworkAssignmentsLoading && (
                        <p className="dashboard-homework-filter-hint" role="status">Chargement des devoirs…</p>
                      )}
                      {!homeworkAssignmentsLoading && homeworkClassAssignments.length >= ASSIGNMENTS_BY_CLASS_UI_LIMIT && (
                        <p className="dashboard-homework-filter-hint dashboard-homework-filter-hint--warn" role="note">
                          Affichage limité aux {ASSIGNMENTS_BY_CLASS_UI_LIMIT} devoirs les plus récents pour cette classe.
                        </p>
                      )}
                      <label className="dashboard-homework-restrict-label">
                        <input
                          type="checkbox"
                          checked={homeworkRestrictOnlyAssigned}
                          onChange={(e) => setHomeworkRestrictOnlyAssigned(e.target.checked)}
                        />
                        <span>N&apos;afficher que les exercices déjà donnés à cette classe</span>
                      </label>
                    </>
                  )}
                </div>
              )}
              {exerciseFilter === 'all' && authors.length > 0 && (
                <div className="dashboard-filter-block">
                  <h4 className="dashboard-filter-block-title">Créateur</h4>
                  <div className="dashboard-chips-container">
                    <button type="button" className={`dashboard-chip ${selectedAuthorId === '' ? 'active' : ''}`} onClick={() => setSelectedAuthorId('')}>Tous</button>
                    {authors.map((a) => (
                      <button key={a.authorId} type="button" className={`dashboard-chip ${selectedAuthorId === a.authorId ? 'active' : ''}`} onClick={() => setSelectedAuthorId(selectedAuthorId === a.authorId ? '' : a.authorId)}>{a.displayName}</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="dashboard-filter-block">
                <h4 className="dashboard-filter-block-title">Niveau</h4>
                <div className="dashboard-difficulty-cards">
                  {DIFFICULTIES.map((d) => (
                    <button key={d} type="button" className={`dashboard-difficulty-card ${selectedDifficulty === d ? 'active' : ''}`} onClick={() => setSelectedDifficulty(selectedDifficulty === d ? '' : d)}>
                      <span className="dashboard-difficulty-icon">{d === 'facile' && '🌱'}{d === 'intermédiaire' && '⭐'}{d === 'difficile' && '🔥'}{d === 'expert' && '💎'}</span>
                      <span className="dashboard-difficulty-label">{d}</span>
                    </button>
                  ))}
                </div>
              </div>
              {composers.length > 0 && (
                <div className="dashboard-filter-block">
                  <h4 className="dashboard-filter-block-title">Compositeur</h4>
                  <div className="dashboard-chips-container">
                    <button type="button" className={`dashboard-chip ${selectedComposer === '' ? 'active' : ''}`} onClick={() => setSelectedComposer('')}>Tous</button>
                    {(composersExpanded ? composers : composers.slice(0, COMPOSERS_VISIBLE_INITIAL)).map((c) => (
                      <button key={c} type="button" className={`dashboard-chip ${selectedComposer === c ? 'active' : ''}`} onClick={() => setSelectedComposer(selectedComposer === c ? '' : c)}>{c}</button>
                    ))}
                    {composers.length > COMPOSERS_VISIBLE_INITIAL && !composersExpanded && (
                      <button type="button" className="dashboard-chip dashboard-chip-load-more" onClick={() => setComposersExpanded(true)}>Charger plus ({composers.length - COMPOSERS_VISIBLE_INITIAL})</button>
                    )}
                  </div>
                </div>
              )}
              {genreOptions.length > 0 && (
                <div className="dashboard-filter-block">
                  <h4 className="dashboard-filter-block-title">Genre</h4>
                  <div className="dashboard-chips-container">
                    <button type="button" className={`dashboard-chip ${selectedGenre === '' ? 'active' : ''}`} onClick={() => setSelectedGenre('')}>Tous</button>
                    {genreOptions.map((g) => (
                      <button key={g.id} type="button" className={`dashboard-chip ${selectedGenre === g.id ? 'active' : ''}`} onClick={() => setSelectedGenre(selectedGenre === g.id ? '' : g.id)}>{g.label}</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="dashboard-filter-block">
                <h4 className="dashboard-filter-block-title">Formation</h4>
                <div className="dashboard-chips-container">
                  <button type="button" className={`dashboard-chip ${selectedFormations.length === 0 ? 'active' : ''}`} onClick={() => setSelectedFormations([])}>Toutes</button>
                </div>
                <FormationInstrumentPicker
                  masterList={CLASSICAL_FORMATIONS}
                  value={selectedFormations}
                  onChange={setSelectedFormations}
                  prioritizedIds={formationCorpusIds}
                  theme="dashboard"
                  showClearAll={false}
                  hint="Instruments déjà présents dans tes exercices en premier — tape pour la liste complète."
                />
              </div>
              {TAG_CATEGORY_KEYS.map((key) => {
                const tagsInCategory = tagCategories[key] || []
                if (tagsInCategory.length === 0) return null
                const isExpanded = expandedTagSections.has(key)
                const label = TAG_CATEGORY_LABELS[key]
                return (
                  <div key={key} className="dashboard-filter-block dashboard-filter-block-tags">
                    <button type="button" className="dashboard-filter-accordion-header" onClick={() => toggleTagSection(key)} aria-expanded={isExpanded}>
                      <span>{label}</span>
                      <span className="dashboard-filter-accordion-icon">{isExpanded ? '▼' : '▶'}</span>
                    </button>
                    {isExpanded && (
                      <div className="dashboard-filter-tag-checkboxes">
                        {tagsInCategory.map((tag) => (
                          <label key={tag} className="dashboard-filter-tag-checkbox">
                            <input type="checkbox" checked={selectedTags.includes(tag)} onChange={() => toggleTag(tag)} />
                            <span>{formatTagForDisplay(tag)}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            {hasActiveFilters && (
              <div className="dashboard-filter-drawer-footer">
                <button type="button" className="dashboard-clear-filters" onClick={() => { clearAllFilters(); setIsFilterDrawerOpen(false) }}>
                  Réinitialiser les filtres
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Floating Action Button */}
      {userData?.role === 'teacher' && (
        <button
          className="dashboard-fab"
          onClick={() => navigate('/editor')}
          aria-label="Créer un nouvel exercice"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </button>
      )}
      
      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        userRole={userData?.role || 'teacher'}
        prefetchedTeacherExercises={
          userData?.role === 'teacher' && exerciseFilter === 'mine' && !loading
            ? exercises
            : undefined
        }
        onNavigate={(path) => {
          navigate(path)
          setIsProfileModalOpen(false)
        }}
      />

      <EditTagsModal
        isOpen={!!editTagsExercise}
        exercise={editTagsExercise}
        onClose={() => setEditTagsExercise(null)}
        onSave={loadExercises}
      />

      <AssignToClassModal
        isOpen={!!assignExercise}
        onClose={() => setAssignExercise(null)}
        exercise={assignExercise}
        teacherId={user?.uid}
        onSuccess={() => {}}
      />

      {statsExercise && (
        <ExerciseStatsModal
          key={statsExercise.id}
          isOpen
          onClose={() => setStatsExercise(null)}
          exercise={statsExercise}
          teacherId={user?.uid}
        />
      )}
    </div>
  )
}

export default Dashboard
